import { Menu, MenuItem, Stack, useTheme } from '@mui/material'
import isNil from 'lodash/isNil'
import { SyntheticEvent, useState } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { RequestPriorityEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { Request } from '@omnea-app/requests/requestsHttpClient'
import { useUpdateRequestProperties } from '../detail/helpers/useUpdateRequestProperties'
import { RequestPriority } from './RequestPriority'

interface Props {
  requestId: string
  priority: Request['derivedFields']['priority']
  displayTitle?: boolean
  displayLabel?: boolean
  displayBorderOnHover?: boolean
  readonly?: boolean
}

const options = [
  RequestPriorityEnum.LOWEST,
  RequestPriorityEnum.LOW,
  RequestPriorityEnum.MEDIUM,
  RequestPriorityEnum.HIGH,
  RequestPriorityEnum.HIGHEST,
]

export const RequestPriorityController = ({
  requestId,
  priority,
  displayTitle = false,
  displayLabel = false,
  displayBorderOnHover = false,
  readonly = false,
}: Props) => {
  const { palette } = useTheme()
  const { user } = useAuthenticatedUser()
  const { mutateAsync: updateRequestProperties, variables, isPending } = useUpdateRequestProperties()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const canUpdateRequestPriority = user.isAdmin && !readonly

  const handleOnClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = (e: SyntheticEvent) => {
    e.stopPropagation()
    setAnchorEl(null)
  }
  const handleUpdateRequestPriority = async (priority: RequestPriorityEnum) => {
    if (!canUpdateRequestPriority) return
    try {
      await updateRequestProperties({ requestId, priority })
    } catch {
      console.info('Failed to update request priority')
    }
    setAnchorEl(null)
  }

  const optimisticRequestPriority = variables?.priority
  const displayedRequestPriority = isPending && !isNil(optimisticRequestPriority) ? optimisticRequestPriority : priority

  return (
    <Stack
      onClick={
        canUpdateRequestPriority
          ? (event) => {
              event.preventDefault()
              handleOnClick(event)
            }
          : undefined
      }
      sx={{
        ...(canUpdateRequestPriority && {
          '&:hover': {
            cursor: 'pointer',
          },
          ...(displayBorderOnHover && {
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: 2,
            '&:hover': {
              borderColor: palette.neutralLight[500],
            },
          }),
        }),
      }}
    >
      <RequestPriority
        priority={displayedRequestPriority}
        displayTitle={displayTitle}
        displayFormattedPriorityString={displayLabel}
        displayTooltip={!displayTitle}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{ mt: 2 }}
      >
        {options.map((itemPriority, i) => {
          return (
            <MenuItem
              key={i}
              sx={{ py: 2, px: 4 }}
              selected={itemPriority === displayedRequestPriority}
              value={itemPriority}
              onClick={() => handleUpdateRequestPriority(itemPriority)}
            >
              <RequestPriority priority={itemPriority} displayFormattedPriorityString />
            </MenuItem>
          )
        })}
      </Menu>
    </Stack>
  )
}
